import {
  IFieldDefinition,
  LayoutElement,
  isBooleanFieldElement,
  isColumnElement,
  isFieldElement,
  isImageElement,
  isInputFieldElement,
  isLayoutTableFieldElement,
  isOptionsFieldElement,
  isTexElement,
} from "@entaconsulting/form";
import { MenuItem, Stack, TextField } from "@mui/material";
import EntaAccordion from "common/components/EntaAccordion";
import { PanelSections } from "developer/edit/constants";
import useDynamicField from "developer/edit/hooks/useDynamicField";
import useIsExpanded from "developer/edit/hooks/useIsExpanded";
import { isBoolean } from "lodash";
import { useState } from "react";
import EditorVisualTextField from "../EditorVisualTextField";
import DynamicField from "./DynamicField";
import AlignImageField from "./commonInputFields/AlignImageField";
import AlignTextField from "./commonInputFields/AlignTextField";
import MarginField from "./commonInputFields/MarginField";
import MultilineField from "./commonInputFields/MultilineField";
import SpacingField from "./commonInputFields/SpacingField";
import VerticalAlignField from "./commonInputFields/VerticalAlignField";

const AppearenceSection = () => {
  const { isExpanded, handleChangeIsExpanded, isDefaultExpanded, hide } =
    useIsExpanded(PanelSections.Appearance);

  const [heightUnit, setHeightUnit] = useState<"percentage" | "pixels">(
    "percentage"
  );
  const {
    input: { value },
  } = useDynamicField("element.height");
  const {
    input: { value: element },
  } = useDynamicField<LayoutElement>("element");
  const {
    input: { value: fieldDef },
  } = useDynamicField<IFieldDefinition>("fieldDef");

  const heightUnitValue =
    typeof value === "string" && value.includes("%") ? "percentage" : "pixeles";

  const isImageEl = isImageElement(element);
  const isTextEl = isTexElement(element);
  const isColumnEl = isColumnElement(element);
  const isFieldEl = isFieldElement(element);
  const isInputFieldEl = isFieldEl && isInputFieldElement(element, fieldDef);
  const isBooleanFieldEl =
    isFieldEl && isBooleanFieldElement(element, fieldDef);
  const isOptionsFieldEl =
    isFieldEl && isOptionsFieldElement(element, fieldDef);
  const isTableFieldEl = isLayoutTableFieldElement(element);

  if (hide) return null;

  return (
    <EntaAccordion
      title="Appearance"
      expanded={isExpanded}
      defaultExpanded={isDefaultExpanded}
      onChange={handleChangeIsExpanded}
    >
      <Stack spacing={1}>
        {isBooleanFieldEl && (
          <DynamicField name="element.variant">
            {(props) => (
              <EditorVisualTextField
                select
                label="Variant"
                size="small"
                fullWidth
                name={props.input.name}
                value={props.input.value}
                onChange={props.input.onChange}
              >
                <MenuItem value="checkbox">Checkbox</MenuItem>
                <MenuItem value="switch">Switch</MenuItem>
              </EditorVisualTextField>
            )}
          </DynamicField>
        )}
        {isOptionsFieldEl && (
          <DynamicField name="element.variant">
            {(props) => (
              <EditorVisualTextField
                select
                label="Variant"
                size="small"
                fullWidth
                name={props.input.name}
                value={props.input.value}
                onChange={props.input.onChange}
              >
                <MenuItem value="select">Select</MenuItem>
                <MenuItem value="combobox">ComboBox</MenuItem>
                <MenuItem value="options">Options</MenuItem>
              </EditorVisualTextField>
            )}
          </DynamicField>
        )}
        {isInputFieldEl && (
          <DynamicField name="fieldDef.inputMask">
            {(props) => (
              <EditorVisualTextField
                label="Mask"
                name={props.input.name}
                value={props.input.value}
                onChange={props.input.onChange}
              />
            )}
          </DynamicField>
        )}
        {isColumnEl && (
          <DynamicField name="element.spacing">
            {(props) => (
              <SpacingField
                name={props.input.name}
                value={props.input.value}
                onChange={props.input.onChange}
              />
            )}
          </DynamicField>
        )}
        {(isImageEl || isTextEl || isColumnEl) && (
          <Stack direction="row" spacing={1}>
            {isImageEl && (
              <DynamicField name="element.align">
                {(props) => (
                  <AlignImageField
                    name={props.input.name}
                    value={props.input.value}
                    onChange={props.input.onChange}
                  />
                )}
              </DynamicField>
            )}
            {isTextEl && (
              <DynamicField name="element.align">
                {(props) => (
                  <AlignTextField
                    name={props.input.name}
                    value={props.input.value}
                    onChange={props.input.onChange}
                  />
                )}
              </DynamicField>
            )}
            {(isImageEl || isTextEl || isColumnEl) && (
              <DynamicField name="element.verticalAlign">
                {(props) => (
                  <VerticalAlignField
                    name={props.input.name}
                    value={props.input.value}
                    onChange={props.input.onChange}
                  />
                )}
              </DynamicField>
            )}
          </Stack>
        )}
        {isImageEl ||
          (isTextEl && (
            <Stack direction="row" spacing={1}>
              {(isImageEl || isTextEl) && (
                <DynamicField name="element.margin">
                  {(props) => (
                    <MarginField
                      name={props.input.name}
                      value={props.input.value}
                      onChange={props.input.onChange}
                    />
                  )}
                </DynamicField>
              )}
              {isTextEl && (
                <DynamicField name="element.weight">
                  {(props) => (
                    <EditorVisualTextField
                      label="Weight"
                      size="small"
                      select
                      fullWidth
                      name={props.input.name}
                      value={props.input.value}
                      onChange={props.input.onChange}
                    >
                      <MenuItem value="light">Light</MenuItem>
                      <MenuItem value="regular">Regular</MenuItem>
                      <MenuItem value="bold">Bold</MenuItem>
                    </EditorVisualTextField>
                  )}
                </DynamicField>
              )}
            </Stack>
          ))}
        {isTextEl && (
          <Stack direction="row" spacing={1}>
            <DynamicField name="element.style">
              {(props) => (
                <EditorVisualTextField
                  select
                  fullWidth
                  label="Font Style"
                  size="small"
                  name={props.input.name}
                  value={props.input.value}
                  onChange={props.input.onChange}
                >
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="italic">Italic</MenuItem>
                </EditorVisualTextField>
              )}
            </DynamicField>
            <DynamicField name="element.textVariant">
              {(props) => (
                <EditorVisualTextField
                  label="Text Variant"
                  size="small"
                  select
                  fullWidth
                  name={props.input.name}
                  value={props.input.value}
                  onChange={props.input.onChange}
                >
                  <MenuItem value="h1">H1</MenuItem>
                  <MenuItem value="h2">H2</MenuItem>
                  <MenuItem value="h3">H3</MenuItem>
                  <MenuItem value="h4">H4</MenuItem>
                  <MenuItem value="h5">H5</MenuItem>
                  <MenuItem value="h6">H6</MenuItem>
                  <MenuItem value="subtitle1">Subtitle1</MenuItem>
                  <MenuItem value="subtitle2">Subtitle2</MenuItem>
                  <MenuItem value="body1">Body1</MenuItem>
                  <MenuItem value="body2">Body2</MenuItem>
                  <MenuItem value="caption">Caption</MenuItem>
                  <MenuItem value="button">Button</MenuItem>
                  <MenuItem value="overline">Overline</MenuItem>
                </EditorVisualTextField>
              )}
            </DynamicField>
          </Stack>
        )}

        {isInputFieldEl && (
          <DynamicField name="element.placeholder">
            {(props) => (
              <EditorVisualTextField
                label="Placeholder"
                size="small"
                fullWidth
                name={props.input.name}
                value={props.input.value}
                onChange={props.input.onChange}
              />
            )}
          </DynamicField>
        )}
        {isInputFieldEl && (
          <DynamicField name="element.prefix">
            {(props) => (
              <EditorVisualTextField
                label="Prefix"
                size="small"
                fullWidth
                name={props.input.name}
                value={props.input.value}
                onChange={props.input.onChange}
              />
            )}
          </DynamicField>
        )}
        {isInputFieldEl && (
          <Stack direction="row" spacing={1} width={1 / 2}>
            <DynamicField name="element.multiline">
              {(props) => (
                <MultilineField
                  name={props.input.name}
                  value={
                    isBoolean(element.multiline)
                      ? element.multiline
                      : fieldDef.type === "multilineText"
                  }
                  onChange={props.input.onChange}
                />
              )}
            </DynamicField>
          </Stack>
        )}
        {isTableFieldEl && (
          <>
            <Stack direction="row" spacing={1}>
              <DynamicField
                name="element.height"
                format={(value) => {
                  if (value) {
                    return heightUnitValue === "percentage" &&
                      typeof value === "string"
                      ? value.split("%")[0]
                      : +value;
                  } else return undefined;
                }}
                parse={(value) => {
                  if (value) {
                    return heightUnitValue === "percentage"
                      ? `${value}%`
                      : +value;
                  }
                }}
              >
                {(props) => (
                  <TextField
                    type="number"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      min: 0,
                    }}
                    label="Table Height"
                    fullWidth
                    size="small"
                    name={props.input.name}
                    value={props.input.value}
                    onChange={props.input.onChange}
                  />
                )}
              </DynamicField>
              <TextField
                select
                label="Unit"
                fullWidth
                size="small"
                value={heightUnit}
                onChange={(event) => setHeightUnit(event.target.value as any)}
              >
                <MenuItem value="percentage">%</MenuItem>
                <MenuItem value="pixels">px</MenuItem>
              </TextField>
            </Stack>
            <Stack direction="row" spacing={1}>
              <DynamicField
                name="element.rowHeight"
                parse={(value) => +value}
                defaultValue={50}
              >
                {(props) => (
                  <TextField
                    type="number"
                    label="Row Height"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      min: 0,
                    }}
                    fullWidth
                    size="small"
                    name={props.input.name}
                    value={props.input.value}
                    onChange={props.input.onChange}
                  />
                )}
              </DynamicField>

              <DynamicField
                name="element.detail.height"
                parse={(value) => +value}
              >
                {(props) => (
                  <TextField
                    type="number"
                    label="Detail Row Height"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      min: 0,
                    }}
                    fullWidth
                    size="small"
                    name={props.input.name}
                    value={props.input.value}
                    onChange={props.input.onChange}
                  />
                )}
              </DynamicField>
            </Stack>
            <Stack direction="row" spacing={1}>
              <DynamicField name="element.scrollMode">
                {(props) => (
                  <TextField
                    select
                    label="Table Scroll Mode"
                    fullWidth
                    size="small"
                    name={props.input.name}
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{
                      shrink: props.input.value === "" ? true : undefined,
                    }}
                    value={props.input.value}
                    onChange={props.input.onChange}
                  >
                    <MenuItem value="">Auto</MenuItem>
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="scrollable">Scrollable</MenuItem>
                    <MenuItem value="virtual">Virtual</MenuItem>
                  </TextField>
                )}
              </DynamicField>
            </Stack>
          </>
        )}
      </Stack>
    </EntaAccordion>
  );
};

export default AppearenceSection;
